import { useCallback, useContext, useEffect } from "react";
import PropTypes from "prop-types";
import {
	ALERT_TYPE,
	CIVILITY,
	FS_PAYMENT_MODE_EVENT_NAME,
	PAYMENT_METHODS,
	PAYMENT_TERMS,
	USABLE_CREDITS_STATUS,
} from "app/constants";
import { FormattedMessage } from "react-intl";
import "./PaymentDataModeFirst.scss";
import AlertMessage from "app/pages/.shared/AlertMessage";
import { sendTagOnPaymentModeSelect, sendTagOnPaymentTypeSelect } from "app/utils/analytics";
import get from "lodash/get";
import lodashValues from "lodash/values";
import { bookingPropTypes, paymentTypeShape } from "app/utils/propTypes";
import PaymentMethodsFieldsetNew from "app/pages/Booking/Payment/PaymentData/PaymentDataModeFirst/PaymentMethodsFieldsetNew/PaymentMethodsFieldsetNew";
import PaymentModesFieldset from "app/pages/Booking/Payment/PaymentData/PaymentDataModeFirst/PaymentModes/PaymentModesFieldset";
import Separator from "app/pages/.shared/Separator";
import usePrevious from "app/utils/hooks/usePrevious";
import InfoPopover from "app/components/ui/InfoPopover/InfoPopover";
import { EventCategory, HitType, useFlagship } from "@flagship.io/react-sdk";
import CreditNoteRadio from "app/pages/Booking/Payment/PaymentCoupons/CreditNoteRadio/CreditNoteRadio";
import {
	sendTagOnCGUCreditNoteClick,
	sendTagOnOnlyNonApplicableCouponMessageShow,
} from "app/utils/analytics";
import OnlyNonApplicableCouponMessage from "app/pages/Booking/Payment/PaymentCoupons/OnlyNonApplicableCouponMessage/OnlyNonApplicableCouponMessage";
import CouponsCguPopoverButton from "app/pages/Booking/Payment/PaymentCoupons/CouponsCGUPopoverButton/CouponsCGUPopoverButton";
import LoaderBar from "app/pages/.shared/LoaderBar/LoaderBar";
import PaymentModesTooltipContainer from "app/pages/Booking/Payment/PaymentData/PaymentDataModeFirst/PaymentModes/PaymentModesTooltipContainer";
import { useFormikContext } from "formik";
import { isEmpty } from "lodash";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import {
	getPaymentCardValues,
	getPaymentFieldNames,
	setNestedFieldErrors,
} from "app/utils/paymentUtils";
import FormErrorMessages from "app/pages/.shared/form/FormErrorMessages/FormErrorMessages";
import { clean } from "fast-clean";
import AppGlobalsContext from "app/AppGlobalsContext";
import { ICON_SIZES } from "app/constants";

const PaymentDataModeFirst = ({
	paymentModes = [],
	isExternalPaymentError,
	isPaymentError,
	error,
	allowedPaymentTypeOfSelectedTerm = [],
	total,
	payNowAmount,
	restoreSessionDepositToPay,
	mensualite2Fois,
	onCreditCardInputsBlur,
	onAdyenComponentMounted = () => {},
	onCreditCardSubmit = () => {},
	onAdyenAdditionalDetails = () => {},
	swissbilling = { forbiddenCause: "", cgvURL: "" },
	paymentModesRef,
	paymentMethodsRef,
	isDepositEnabled,
	isBookingEligibleForDeposit,
	isPaiementPledg4xAvailable,
	isPaymentAlmaAvailable,
	isPaymentFloaAvailable,
	selectedPaymentMode,
	selectedPaymentType,
	updateBooking,
	booking = {},
	paymentTypes = [],
	usableCreditNotes = [],
	unusableCreditNotes = [],
	onlyNonApplicableCreditNotes = [],
	totalUsableCreditNote,
	usableCouponsStatus,
	setPaymentError = () => {},
}) => {
	const previousBookingMode = usePrevious(booking.paymentMode);
	const previousBookingType = usePrevious(booking.paymentType);

	const { sendHits } = useFlagship();
	const {
		values,
		errors,
		setFieldError,
		setFieldValue,
		isSubmitting,
		submitCount,
	} = useFormikContext();

	const { isOperatorMode } = useContext(AppGlobalsContext);

	const prevPaymentValues = usePrevious(values?.payment);

	useEffect(() => {
		/**
		 * A chaque fois que l'on change le mode de paiement, on verifie si le type de paiement actuellement
		 * selectionné correspond bien au nouveau mode de paiement selectionné.
		 * Si ce n'est pas le cas, on selectionne le premier type de paiement disponible et qui correspondant
		 * au nouveau mode de paiement selectionné
		 */
		if (booking && booking.paymentMode !== previousBookingMode) {
			let paymentTypetoSelect = get(allowedPaymentTypeOfSelectedTerm, "[0].paymentType");

			const paymentTypesOfModes = paymentTypes.filter(
				paymentType => paymentType.terms === booking.paymentMode
			);

			if (
				booking.paymentType &&
				lodashValues(
					paymentTypesOfModes.map(allowedPaymentType => allowedPaymentType.paymentType)
				).includes(booking.paymentType)
			) {
				paymentTypetoSelect = booking.paymentType;
			}
			updateBooking({
				paymentType: paymentTypetoSelect,
			});
			setFieldValue("payment.type", paymentTypetoSelect);
		}
	}, [booking.paymentMode, values?.payment, paymentTypes, previousBookingMode]);

	useEffect(() => {
		if (
			previousBookingType !== booking.paymentType ||
			previousBookingMode !== booking.paymentMode
		) {
			const previousPaymentCardObject = getPaymentCardValues(previousBookingType);
			const fieldNames = getPaymentFieldNames(previousPaymentCardObject, "payment.");
			if (!isEmpty(fieldNames)) {
				fieldNames.forEach(name => setFieldValue(name, ""));
			}
		}
	}, [previousBookingMode, booking.paymentType, previousBookingType, booking.paymentMode]);

	const handlePaymentModeChange = useCallback(
		paymentMode => {
			updateBooking({ paymentMode });

			sendHits({
				type: HitType.EVENT,
				category: EventCategory.ACTION_TRACKING,
				action: FS_PAYMENT_MODE_EVENT_NAME[paymentMode],
			});

			sendTagOnPaymentModeSelect(paymentMode);
			if (!isEmpty(clean(errors?.payment))) {
				setNestedFieldErrors("payment", clean(errors?.payment), setFieldError);
			}
		},
		[updateBooking, errors, setFieldError]
	);

	const handlePaymentTypeChange = useCallback(
		paymentType => {
			// permet de mettre a jour le label du bouton en fonction du moyen de paiement
			updateBooking({ paymentType });
			sendTagOnPaymentTypeSelect(paymentType);
			if (!isEmpty(clean(errors?.payment))) {
				setNestedFieldErrors("payment", clean(errors?.payment), setFieldError);
			}
		},
		[updateBooking, errors, setFieldError]
	);

	useEffect(() => {
		if (
			!isEmpty(prevPaymentValues) &&
			!isEmpty(values?.payment) &&
			prevPaymentValues !== values?.payment
		) {
			if (!isEmpty(clean(error)) && (isPaymentError || isExternalPaymentError)) {
				setPaymentError({});
			}
		}
	}, [error, isPaymentError, isExternalPaymentError, values?.payment, prevPaymentValues]);

	const handlePaymentBankChange = useCallback(
		paymentBank => {
			updateBooking({ paymentBank });
		},
		[updateBooking]
	);

	const isPaymentPledgSelected =
		isPaiementPledg4xAvailable && selectedPaymentMode === PAYMENT_TERMS["4x"];
	const isPaymentAlmaSelected = selectedPaymentType === PAYMENT_METHODS.ALMA;
	const isPaymentFloaSelected = selectedPaymentType === PAYMENT_METHODS.FLOA;
	const isPaymentCheckoutSelected = selectedPaymentType === PAYMENT_METHODS.CHECKOUT;

	const hidePaymentMethodsFieldset =
		isPaymentPledgSelected ||
		isPaymentAlmaSelected ||
		isPaymentFloaSelected ||
		(isPaymentCheckoutSelected && allowedPaymentTypeOfSelectedTerm?.length === 1) ||
		isOperatorMode;

	const hasCreditNotes = totalUsableCreditNote > 0;
	const hasOnlyNonApplicableCreditNotes =
		!hasCreditNotes &&
		unusableCreditNotes.length === onlyNonApplicableCreditNotes.length &&
		onlyNonApplicableCreditNotes.length > 0;
	const isLoadingCreditNotes = usableCouponsStatus === USABLE_CREDITS_STATUS.LOADING_CREDIT_NOTES;
	const handleOnCreditNoteCGUClick = useCallback(() => {
		sendTagOnCGUCreditNoteClick();
	}, []);

	const handleOnOnlyNonApplicableCreditNoteMessageShow = useCallback(() => {
		sendTagOnOnlyNonApplicableCouponMessageShow("refund", onlyNonApplicableCreditNotes);
	}, [onlyNonApplicableCreditNotes]);

	const hasSectionCreditNote =
		hasOnlyNonApplicableCreditNotes || hasCreditNotes || isLoadingCreditNotes;

	return (
		<div>
			{hasSectionCreditNote && (
				<div
					className="payment-data-mode--credit-note"
					data-testid="payment-data-credit-note"
				>
					{hasOnlyNonApplicableCreditNotes ? (
						<OnlyNonApplicableCouponMessage
							title={
								<FormattedMessage id="payment.coupons.credit.note.only.not.applicable.warning.title" />
							}
							message={
								<FormattedMessage id="payment.coupons.credit.note.only.not.applicable.warning.message" />
							}
							coupons={onlyNonApplicableCreditNotes}
							onShow={handleOnOnlyNonApplicableCreditNoteMessageShow}
						/>
					) : (
						<Typography
							variant={TYPOGRAPHY_VARIANTS.LARGE}
							isBold
							className="payment-credits__title"
							component="h3"
						>
							<FormattedMessage id="payment.coupons.title.creditnote" />
							<CouponsCguPopoverButton
								hasCreditNotes={true}
								onClick={handleOnCreditNoteCGUClick}
							/>
						</Typography>
					)}

					{isLoadingCreditNotes && (
						<div className="payment-credits__item">
							<LoaderBar />
						</div>
					)}

					{hasCreditNotes && (
						<div className="payment-credits__item">
							<CreditNoteRadio
								value={totalUsableCreditNote}
								usableCreditNotes={usableCreditNotes}
								unusableCreditNotes={unusableCreditNotes}
							/>
						</div>
					)}
				</div>
			)}
			<div className="payment-data-mode-first" ref={paymentModesRef}>
				{!isEmpty(clean(error)) && (isPaymentError || isExternalPaymentError) && (
					<AlertMessage
						message={<FormattedMessage {...error} />}
						alertType={ALERT_TYPE.ERROR}
					/>
				)}

				{paymentModes.length > 1 && (
					<div className="payment-data-mode-first__options">
						<Typography
							variant={TYPOGRAPHY_VARIANTS.LARGE}
							isBold
							className="payment-data-mode-first__title"
							data-testid="payment-data-mode-first-title-option"
							component="h3"
						>
							{hasSectionCreditNote && (
								<FormattedMessage id="payment.select.option" values={{ step: 2 }} />
							)}
							{!hasSectionCreditNote && (
								<FormattedMessage id="payment.select.option.without.multiple.type" />
							)}

							<InfoPopover
								iconSize={ICON_SIZES.XXS}
								iconStyle={{ marginLeft: "4px" }}
							>
								<PaymentModesTooltipContainer paymentModes={paymentModes} />
							</InfoPopover>
						</Typography>

						<PaymentModesFieldset
							total={restoreSessionDepositToPay || total}
							mensualite2Fois={mensualite2Fois}
							paymentModes={paymentModes}
							showMaidenName={values?.contact["honorific-prefix"] === CIVILITY.FEMALE}
							onChange={handlePaymentModeChange}
							isDepositEnabled={isDepositEnabled}
							isPaymentAlmaAvailable={isPaymentAlmaAvailable}
							isPaymentFloaAvailable={isPaymentFloaAvailable}
							isBookingEligibleForDeposit={isBookingEligibleForDeposit}
							data-testid="payment-modes-fieldset"
						/>
					</div>
				)}

				{hidePaymentMethodsFieldset ? null : (
					<div
						className="payment-data-mode-first__methods"
						ref={paymentMethodsRef}
						data-testid="payment-data-mode-first--methods"
					>
						{paymentModes.length > 1 && <Separator design="middle" />}
						<FormErrorMessages
							errors={errors}
							message={
								<FormattedMessage id="error.booking.payment.fields.required" />
							}
							disableErrorsDetails={
								selectedPaymentType === PAYMENT_METHODS.ADYEN_FLOW ||
								selectedPaymentType === PAYMENT_METHODS.CARD
							}
							fieldsFilter={["payment"]}
							isSubmitting={isSubmitting}
							submitCount={submitCount}
						/>

						<PaymentMethodsFieldsetNew
							swissbilling={swissbilling}
							mensualite2Fois={mensualite2Fois}
							total={payNowAmount || total}
							allowedPaymentTypes={allowedPaymentTypeOfSelectedTerm}
							onChange={handlePaymentTypeChange}
							ideal={{ onPaymentBankChange: handlePaymentBankChange }}
							onCreditCardInputsBlur={onCreditCardInputsBlur}
							onAdyenComponentMounted={onAdyenComponentMounted}
							onCreditCardSubmit={onCreditCardSubmit}
							onAdyenAdditionalDetails={onAdyenAdditionalDetails}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

PaymentDataModeFirst.propTypes = {
	swissbilling: PropTypes.object,
	paymentTypes: PropTypes.arrayOf(paymentTypeShape),
	paymentModes: PropTypes.arrayOf(PropTypes.number),
	updateBooking: PropTypes.func,
	onPaymentTypeChange: PropTypes.func,
	isPaymentError: PropTypes.bool,
	isExternalPaymentError: PropTypes.bool,
	error: PropTypes.object,
	booking: bookingPropTypes,
	onAdyenComponentMounted: PropTypes.func,
	onAdyenAdditionalDetails: PropTypes.func,
	onCreditCardSubmit: PropTypes.func,
	onCreditCardInputsBlur: PropTypes.func,
	onChange: PropTypes.func,
	allowedPaymentTypeOfSelectedTerm: PropTypes.arrayOf(paymentTypeShape),
	total: PropTypes.number,
	payNowAmount: PropTypes.number,
	restoreSessionDepositToPay: PropTypes.number,
	mensualite2Fois: PropTypes.object,
	swissBillingCGVURL: PropTypes.string,
	showAmountToPay: PropTypes.bool,
	paymentModesRef: PropTypes.object,
	paymentMethodsRef: PropTypes.object,
	isDepositEnabled: PropTypes.bool,
	isBookingEligibleForDeposit: PropTypes.bool,
	isPaiementPledg4xAvailable: PropTypes.bool,
	isPaymentAlmaAvailable: PropTypes.bool,
	isPaymentFloaAvailable: PropTypes.bool,
	selectedPaymentMode: PropTypes.number,
	selectedPaymentType: PropTypes.string,
	usableCreditNotes: PropTypes.array,
	unusableCreditNotes: PropTypes.array,
	totalUsableCreditNote: PropTypes.number,
	onlyNonApplicableCreditNotes: PropTypes.array,
	usableCouponsStatus: PropTypes.string,
	setPaymentError: PropTypes.func,
};

export default PaymentDataModeFirst;
